import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { DEFAULT_PERMISSIONS, DEFAULT_SETTINGS, MEMBER_CLASSES, DEFAULT_MEMBER_CLASSES, CLASS_LABELS } from '../mock';
import { ADMIN_SECTIONS } from '../lib/adminSections';
import { setFavicon } from '../lib/favicon';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const http = axios.create({ baseURL: API, withCredentials: true });

const AuthContext = createContext(null);

function normalize(doc) {
  if (!doc) return null;
  return { ...doc, memberClass: doc.member_class };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [requestedPreview] = useState(() => {
    try {
      const v = new URLSearchParams(window.location.search).get('viewAs');
      return v && v !== 'admin' ? v : null;
    } catch (e) { return null; }
  });
  const [previewCleared, setPreviewCleared] = useState(false);
  const [users, setUsers] = useState([]);
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [customPages, setCustomPages] = useState([]);
  const [builtinOverrides, setBuiltinOverrides] = useState({});
  const [org, setOrg] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const fetchOrg = useCallback(async () => {
    try {
      const { data } = await http.get('/organisation');
      if (data && typeof data === 'object') {
        setOrg(data);
        if (data.name) document.title = data.name;
        setFavicon(data.favicon_url);
      }
    } catch (e) {
      setOrg(null);
    }
  }, []);

  const fetchBuiltinOverrides = useCallback(async () => {
    try {
      const { data } = await http.get('/builtin-overrides');
      setBuiltinOverrides(data && typeof data === 'object' ? data : {});
    } catch (e) {
      setBuiltinOverrides({});
    }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const { data } = await http.get('/permissions');
      if (data && Object.keys(data).length) setPermissions(data);
    } catch (e) {
      console.error('Failed to load permissions; using defaults', e);
    }
  }, []);

  const fetchPages = useCallback(async () => {
    try {
      const { data } = await http.get('/pages');
      setCustomPages(Array.isArray(data) ? data : []);
    } catch (e) {
      setCustomPages([]);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    try {
      const { data } = await http.get('/settings');
      if (data && data.colors) setSettings(data);
    } catch (e) {
      console.error('Failed to load site settings; using defaults', e);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await http.get('/auth/me');
      setUser(normalize(data));
    } catch (e) {
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      await Promise.all([fetchPermissions(), fetchSettings(), fetchPages(), fetchBuiltinOverrides(), fetchOrg()]);
      // CRITICAL: if returning from OAuth callback, skip the /me check.
      if (window.location.hash && window.location.hash.includes('session_id=')) {
        setLoaded(true);
        return;
      }
      await checkAuth();
      setLoaded(true);
    };
    init();
  }, [checkAuth, fetchPermissions, fetchSettings, fetchPages, fetchBuiltinOverrides, fetchOrg]);

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const loginWithGoogle = () => {
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processSessionId = async (sessionId) => {
    const { data } = await http.post('/auth/session', { session_id: sessionId }, {
      headers: { 'X-Session-ID': sessionId },
    });
    setUser(normalize(data));
    await fetchPermissions();
    return normalize(data);
  };

  // ---- Email + password auth ----
  const login = async (email, password) => {
    const { data } = await http.post('/auth/login', { email, password });
    setUser(normalize(data));
    await fetchPermissions();
    return normalize(data);
  };
  const register = async (email, password, name) => {
    const { data } = await http.post('/auth/register', { email, password, name });
    setUser(normalize(data));
    await fetchPermissions();
    return normalize(data);
  };
  const forgotPassword = async (email) =>
    (await http.post('/auth/forgot-password', { email, origin: window.location.origin })).data;
  const resetPassword = async (token, password) => {
    const { data } = await http.post('/auth/reset-password', { token, password });
    if (data && data.user_id) { setUser(normalize(data)); await fetchPermissions(); }
    return data;
  };
  const setUserPassword = async (userId, password) =>
    (await http.put(`/admin/users/${userId}/password`, { password })).data;
  const getEmailSettings = async () => (await http.get('/admin/email-settings')).data;
  const updateEmailSettings = async (payload) => (await http.put('/admin/email-settings', payload)).data;
  const sendTestEmail = async (to) => (await http.post('/admin/email-settings/test', { to })).data;
  const getHasPassword = async () => (await http.get('/auth/has-password')).data;
  const getMyActivity = async () => (await http.get('/my/activity')).data;
  const listEvents = async () => (await http.get('/events')).data;
  const registerEvent = async (id, body) => (await http.post(`/events/${id}/register`, body)).data;
  const adminListEvents = async () => (await http.get('/admin/events')).data;
  const adminSaveEvent = async (id, body) => id ? (await http.put(`/admin/events/${id}`, body)).data : (await http.post('/admin/events', body)).data;
  const adminDeleteEvent = async (id) => (await http.delete(`/admin/events/${id}`)).data;
  const adminEventRegs = async (id) => (await http.get(`/admin/events/${id}/registrations`)).data;
  const setMyPassword = async (password, currentPassword) =>
    (await http.post('/auth/set-password', { password, current_password: currentPassword })).data;
  const getAnalyticsSummary = async (days) => (await http.get('/admin/analytics/summary', { params: days ? { days } : {} })).data;
  const getAnalyticsLog = async (limit = 100, skip = 0) => (await http.get('/admin/analytics/log', { params: { limit, skip } })).data;
  const analyticsExportUrl = () => `${API}/admin/analytics/export`;
  const getMemberActivity = async (userId) => (await http.get(`/admin/members/${userId}/activity`)).data;
  // ---- Member photo gallery ----
  const uploadGalleryPhoto = async (dataUrl, contentType) => (await http.post('/my/photos', { data: dataUrl, content_type: contentType || 'image/jpeg' })).data;
  const listMyPhotos = async () => (await http.get('/my/photos')).data;
  const listMyAlbums = async () => (await http.get('/my/albums')).data;
  const updateMyPhoto = async (photoId, meta) => (await http.patch(`/my/photos/${photoId}`, meta)).data;
  const deleteMyPhoto = async (photoId) => (await http.delete(`/my/photos/${photoId}`)).data;
  const getPhotoGallery = async () => (await http.get('/photos/gallery')).data;
  const listPhotoAlbums = async () => (await http.get('/photos/albums')).data;
  const getPhotoAlbum = async (name) => (await http.get(`/photos/albums/${encodeURIComponent(name)}`)).data;
  const adminSetAlbumMeta = async (name, body) => (await http.put(`/admin/photos/albums/${encodeURIComponent(name)}`, body)).data;
  const getTagsUsed = useCallback(async (area) => (await http.get('/tags/used', { params: { area } })).data, []);
  const actionMembers = useCallback(async () => (await http.get('/actions/members')).data, []);
  const createAction = async (body) => (await http.post('/actions', body)).data;
  const myActions = useCallback(async () => (await http.get('/actions/mine')).data, []);
  const listActions = useCallback(async (params) => (await http.get('/actions', { params })).data, []);
  const actionsBySource = useCallback(async (type, id) => (await http.get(`/actions/by-source/${type}/${id}`)).data, []);
  const patchAction = async (id, body) => (await http.patch(`/actions/${id}`, body)).data;
  const delegateAction = async (id, body) => (await http.post(`/actions/${id}/delegate`, body)).data;
  const deleteAction = async (id) => (await http.delete(`/actions/${id}`)).data;
  const bulkActions = async (body) => (await http.post('/actions/bulk', body)).data;
  const requestDetailsUpdate = async (userIds) => (await http.post('/admin/members/request-details', { user_ids: userIds })).data;
  const confirmMyDetails = async () => (await http.post('/my/details/confirm')).data;
  const getDigestPref = useCallback(async () => (await http.get('/my/action-digest')).data, []);
  const setDigestPref = async (period) => (await http.put('/my/action-digest', { period })).data;
  const previewDigest = async () => (await http.get('/my/action-digest/preview')).data;
  const getMyLog = useCallback(async () => (await http.get('/my/log')).data, []);
  const dataTableSources = useCallback(async () => (await http.get('/data-table/sources')).data, []);
  const dataTableRows = useCallback(async (source, tag) => (await http.get('/data-table', { params: { source, ...(tag ? { tag } : {}) } })).data, []);
  const listPhotoComments = async (photoId) => (await http.get(`/photos/${photoId}/comments`)).data;
  const addPhotoComment = async (photoId, text) => (await http.post(`/photos/${photoId}/comments`, { text })).data;
  const deletePhotoComment = async (photoId, cid) => (await http.delete(`/photos/${photoId}/comments/${cid}`)).data;
  const adminAlbumShareLink = async (name) => (await http.get(`/admin/photos/albums/${encodeURIComponent(name)}/share-link`)).data;
  const getMemberPhotos = async (userId) => (await http.get(`/photos/member/${userId}`)).data;
  const adminListPhotos = async (status) => (await http.get('/admin/photos', { params: status ? { status } : {} })).data;
  const moderatePhoto = async (photoId, action, note) => (await http.post(`/admin/photos/${photoId}/moderate`, null, { params: { action, note: note || '' } })).data;
  const adminUpdatePhoto = async (id, body) => (await http.patch(`/admin/photos/${id}`, body)).data;
  const adminDeletePhoto = async (id) => (await http.delete(`/admin/photos/${id}`)).data;
  const adminReplacePhotoImage = async (id, data) => (await http.post(`/admin/photos/${id}/image`, { data })).data;
  const adminAiDescribePhoto = async (id, overwrite = false) => (await http.post(`/admin/photos/${id}/ai-describe`, { overwrite }, { timeout: 90000 })).data;
  const adminFaceSuggest = async (id) => (await http.post(`/admin/photos/${id}/face-suggest`, {}, { timeout: 90000 })).data;
  const adminPhotoPeople = async (id, body) => (await http.post(`/admin/photos/${id}/people`, body)).data;
  const adminBulkPhotos = async (body) => (await http.post('/admin/photos/bulk', body)).data;
  const adminPhotoReviewDigestNow = async () => (await http.post('/admin/photos/review-digest-now')).data;
  const adminPhotoAlbums = async () => (await http.get('/admin/photos/albums')).data;
  const adminAlbums = async () => (await http.get('/admin/albums')).data;
  const adminCreateAlbum = async (body) => (await http.post('/admin/albums', body)).data;
  const adminUpdateAlbum = async (name, body) => (await http.patch(`/admin/albums/${encodeURIComponent(name)}`, body)).data;
  const adminDeleteAlbum = async (name) => (await http.delete(`/admin/albums/${encodeURIComponent(name)}`)).data;
  const adminAlbumPhotos = async (name) => (await http.get(`/admin/albums/${encodeURIComponent(name)}/photos`)).data;
  const adminAlbumAddPhotos = async (name, ids) => (await http.post(`/admin/albums/${encodeURIComponent(name)}/photos`, { ids })).data;
  const adminAlbumRemovePhotos = async (name, ids) => (await http.post(`/admin/albums/${encodeURIComponent(name)}/photos/remove`, { ids })).data;
  const adminAlbumUpload = async (name, body) => (await http.post(`/admin/albums/${encodeURIComponent(name)}/upload`, body, { timeout: 120000 })).data;
  const adminAlbumReorder = async (name, ids) => (await http.post(`/admin/albums/${encodeURIComponent(name)}/reorder`, { ids })).data;
  const getAppointmentRoles = async () => (await http.get('/appointment-roles')).data;
  const getOfficeBearers = async () => (await http.get('/office-bearers')).data;
  const adminEventPhotos = async (eid) => (await http.get(`/admin/events/${eid}/photos`)).data;
  const adminUploadEventPhoto = async (eid, body) => (await http.post(`/admin/events/${eid}/photos`, body)).data;
  const adminAttachEventPhotos = async (eid, body) => (await http.post(`/admin/events/${eid}/photos/attach`, body)).data;
  const adminReorderEventPhotos = async (eid, body) => (await http.post(`/admin/events/${eid}/photos/reorder`, body)).data;
  const getWeeklyDigest = async () => (await http.get('/admin/weekly-digest')).data;
  const updateWeeklyDigest = async (payload) => (await http.put('/admin/weekly-digest', payload)).data;
  const sendWeeklyDigestNow = async () => (await http.post('/admin/weekly-digest/send')).data;

  const logout = async () => {
    try {
      await http.post('/auth/logout');
    } catch (e) {
      // ignore
    }
    setUser(null);
  };

  const updatePermissions = async (next) => {
    const { data } = await http.put('/admin/permissions', { permissions: next });
    setPermissions(data);
    return data;
  };

  const refreshUsers = useCallback(async () => {
    try {
      const { data } = await http.get('/admin/users');
      setUsers(data.map(normalize));
    } catch (e) {
      setUsers([]);
    }
  }, []);

  const updateUserClass = async (userId, memberClass) => {
    await http.put(`/admin/users/${userId}`, { member_class: memberClass });
    await refreshUsers();
    if (user && user.user_id === userId) {
      setUser({ ...user, member_class: memberClass, memberClass });
    }
  };

  const adminCreateContact = async (body) => (await http.post('/admin/users', body)).data;
  const adminDeleteContact = async (userId) => (await http.delete(`/admin/users/${userId}`)).data;
  const adminInviteContact = async (userId, message) => (await http.post(`/admin/users/${userId}/invite`, { message })).data;
  const adminContactMessage = async (id) => (await http.get(`/admin/contact-messages/${id}`)).data;
  const adminContactMessages = async (status = 'open') => (await http.get('/admin/contact-messages', { params: { status } })).data;
  const adminContactMessageHandled = async (id, handled, note) => (await http.post(`/admin/contact-messages/${id}/handled`, { handled, note })).data;
  const welcomePending = async () => (await http.get('/admin/welcome/pending')).data;
  const saveWelcomeSettings = async (body) => (await http.put('/admin/welcome/settings', body)).data;
  const welcomeRun = async () => (await http.post('/admin/welcome/run')).data;
  const welcomePreview = async (userId, invite, message) => (await http.post(`/admin/welcome/${userId}/preview`, { invite, message })).data;
  const welcomeSend = async (userId, invite, message) => (await http.post(`/admin/welcome/${userId}/send`, { invite, message })).data;
  const welcomeSkip = async (userId) => (await http.post(`/admin/welcome/${userId}/skip`)).data;
  const emailQueueList = async (status = 'held') => (await http.get('/admin/email-queue', { params: { status } })).data;
  const emailQueueCount = async () => (await http.get('/admin/email-queue/count')).data;
  const emailQueueItem = async (id) => (await http.get(`/admin/email-queue/${id}`)).data;
  const emailQueueEdit = async (id, body) => (await http.put(`/admin/email-queue/${id}`, body)).data;
  const emailQueueRelease = async (id) => (await http.post(`/admin/email-queue/${id}/release`)).data;
  const emailQueueAbort = async (id, reason) => (await http.post(`/admin/email-queue/${id}/abort`, { reason })).data;
  const emailQueueRefreshContact = async (id) => (await http.post(`/admin/email-queue/${id}/refresh-contact`)).data;
  const emailQueueBulk = async (ids, action) => (await http.post('/admin/email-queue/bulk', { ids, action })).data;
  const emailQueueDelete = async (id) => (await http.delete(`/admin/email-queue/${id}`)).data;
  const emailQueueSettings = async () => (await http.get('/admin/email-queue/settings')).data;
  const saveEmailQueueSettings = async (body) => (await http.put('/admin/email-queue/settings', Array.isArray(body) ? { hold_kinds: body } : body)).data;
  const emailQueueTemplateProposal = async (id) => (await http.post(`/admin/email-queue/${id}/template-proposal`)).data;
  const emailQueueApplyTemplate = async (id, body) => (await http.post(`/admin/email-queue/${id}/apply-template`, body)).data;
  const emailQueueAutoReleaseRun = async () => (await http.post('/admin/email-queue/auto-release/run')).data;
  const emailQueueSummaryRun = async () => (await http.post('/admin/email-queue/summary/run')).data;
  const updateUserFields = async (userId, fields) => {
    await http.put(`/admin/users/${userId}`, fields);
    await refreshUsers();
  };

  const setUserTags = async (userId, tags) => {
    await http.put(`/admin/users/${userId}/tags`, { tags });
    await refreshUsers();
  };
  const bulkUserTags = async (payload) => {
    await http.post('/admin/users/tags/bulk', payload);
    await refreshUsers();
  };
  const getGroupRecipients = async (tags, match, includeInactive = false) =>
    (await http.get('/admin/email/recipients', { params: { tags: tags.join(','), match, include_inactive: includeInactive } })).data;
  const sendGroupEmail = async (payload) => (await http.post('/admin/email/group', payload)).data;
  const getGroupEmailHistory = async () => (await http.get('/admin/email/history')).data;
  const simulateInbound = async (payload) => (await http.post('/admin/inbox/simulate', payload)).data;
  const simulateVoteReply = async (payload) => (await http.post('/admin/inbox/simulate-vote-reply', payload)).data;
  const qaAccountCheck = async () => (await http.get('/admin/security/qa-account-check')).data;
  const productionReadiness = async () => (await http.get('/admin/security/production-readiness')).data;
  const getReadinessSettings = async () => (await http.get('/admin/security/readiness-report/settings')).data;
  const updateReadinessSettings = async (body) => (await http.put('/admin/security/readiness-report/settings', body)).data;
  const sendReadinessReport = async () => (await http.post('/admin/security/readiness-report/send')).data;
  const getAbout = async () => (await http.get('/about')).data;
  const saveAbout = async (body) => (await http.put('/admin/about', body)).data;
  const getBrandColors = async () => (await http.get('/brand-colors')).data;
  const saveBrandColors = async (colors) => (await http.put('/admin/brand-colors', { colors })).data;
  const remindNonVoters = async (id, motionId) => (await http.post(`/admin/meetings/${id}/motions/${motionId}/remind`)).data;
  const getReadinessHistory = async () => (await http.get('/admin/security/readiness-history')).data;
  const downloadTestPlan = async (kind) => {
    const res = await http.get(`/admin/test-plan/${kind}`, { responseType: 'blob' });
    const names = { 'plan-md': 'MANUAL_TEST_PLAN.md', 'plan-csv': 'manual_test_plan.csv', 'smoke-csv': 'smoke_test_sheet.csv' };
    const url = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = url; a.download = names[kind] || 'test-plan';
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  };
  const getVoteLog = async () => (await http.get('/admin/inbox/vote-log')).data;
  const getForwardLog = async () => (await http.get('/admin/inbox/forward-log')).data;
  const getInboxConfig = async () => (await http.get('/admin/inbox/config')).data;
  const getImapSettings = async () => (await http.get('/admin/inbox/imap')).data;
  const updateImapSettings = async (patch) => (await http.put('/admin/inbox/imap', patch)).data;
  const testImap = async () => (await http.post('/admin/inbox/imap/test')).data;
  const pollImapNow = async () => (await http.post('/admin/inbox/imap/poll-now')).data;
  const importMembers = async (rows) => {
    const { data } = await http.post('/admin/members/import', { rows });
    await refreshUsers();
    return data;
  };
  const importMembersCsv = async (csv) => {
    const { data } = await http.post('/admin/members/import-csv', { csv });
    await refreshUsers();
    return data;
  };
  const saveBuiltinOverride = async (pageKey, fields) => {
    await http.put(`/admin/builtin/${pageKey}`, fields);
    await fetchBuiltinOverrides();
  };
  const resetBuiltinOverride = async (pageKey) => {
    await http.delete(`/admin/builtin/${pageKey}`);
    await fetchBuiltinOverrides();
  };

  const getDirectory = async () => (await http.get('/members/directory')).data;
  const getOrg = async () => (await http.get('/admin/organisation')).data;
  const updateOrg = async (fields) => {
    const { data } = await http.put('/admin/organisation', fields);
    setOrg({
      name: data.name, short_name: data.short_name, tagline: data.tagline,
      member_noun: data.member_noun, currency: data.currency, timezone: data.timezone,
      website: data.website, favicon_url: data.favicon_url, setup_complete: data.setup_complete,
    });
    if (data.name) document.title = data.name;
    setFavicon(data.favicon_url);
    return data;
  };
  const getMemberProfile = async (memberId) => (await http.get(`/members/directory/${memberId}`)).data;
  const receiptUrl = (sessionId) => `${API}/payments/receipt/${sessionId}`;
  // Uploaded images saved with an old host (e.g. a previous preview URL) are re-pointed at the current backend.
  const mediaUrl = (path) => {
    if (!path) return '';
    const m = /^https?:\/\/[^/]+(\/api\/images\/.+)$/.exec(path);
    if (m) return `${BACKEND_URL}${m[1]}`;
    return path.startsWith('http') ? path : `${BACKEND_URL}${path}`;
  };

  // ---- Member profile photos (multiple + external/Google sources) ----
  const uploadMyPhoto = async (dataUrl, contentType) =>
    (await http.post('/members/me/photo', { data: dataUrl, content_type: contentType })).data;
  const addPhotoUrl = async (url) => (await http.post('/members/me/photo/url', { url })).data;
  const addGooglePhoto = async () => (await http.post('/members/me/photo/google')).data;
  const setPrimaryPhoto = async (photoId) => (await http.put(`/members/me/photo/${photoId}/primary`)).data;
  const deletePhoto = async (photoId) => (await http.delete(`/members/me/photo/${photoId}`)).data;
  const clearMyPhotos = async () => (await http.delete('/members/me/photo')).data;
  const updateMyProfile = async (fields) => (await http.put('/members/me', fields)).data;

  // ---- Renewals ----
  const getRenewals = async (days = 30, includeHidden = false) => (await http.get(`/admin/renewals?days=${days}&include_hidden=${includeHidden}`)).data;
  const runRenewals = async (days = 30) => (await http.post(`/admin/renewals/run?days=${days}`)).data;
  const sendRenewal = async (userId) => (await http.post(`/admin/renewals/send/${userId}`)).data;
  const membershipSettings = async () => (await http.get('/admin/membership-settings')).data;
  const saveMembershipSettings = async (body) => (await http.put('/admin/membership-settings', body)).data;
  const setMemberExpiry = async (userId, membership_expiry, note) => (await http.put(`/admin/renewals/${userId}/expiry`, { membership_expiry, note })).data;
  const renewalInvoiceSettings = async () => (await http.get('/admin/renewals/invoices/settings')).data;
  const saveRenewalInvoiceSettings = async (body) => (await http.put('/admin/renewals/invoices/settings', body)).data;
  const runRenewalInvoices = async () => (await http.post('/admin/renewals/invoices/run')).data;
  const previewRenewalInvoice = async (userId) => (await http.get(`/admin/renewals/${userId}/invoice-preview`)).data;
  const lapsedMembers = async () => (await http.get('/admin/renewals/lapsed')).data;
  const reinstateMember = async (userId) => (await http.post(`/admin/renewals/${userId}/reinstate`)).data;
  const renewalApprovals = useCallback(async () => (await http.get('/admin/renewals/approvals')).data, []);
  const decideRenewalApproval = async (id, decision, note = '') => (await http.post(`/admin/renewals/approvals/${id}/${decision}`, { note })).data;
  const runLapseSweep = async () => (await http.post('/admin/renewals/lapse-sweep/run')).data;
  const previewLapseSweep = async () => (await http.get('/admin/renewals/lapse-sweep/preview')).data;
  const previewGraceWarnings = async () => (await http.get('/admin/renewals/grace-warnings/preview')).data;
  const runGraceWarnings = async () => (await http.post('/admin/renewals/grace-warnings/run')).data;
  const lapsedReportPdf = async () => { const r = await http.get('/admin/renewals/lapsed/report', { params: { format: 'pdf' }, responseType: 'blob' }); saveBlob(r.data, `lapsed-members-${new Date().toISOString().slice(0, 10)}.pdf`); };
  const lapsedReportPrint = async () => { const r = await http.get('/admin/renewals/lapsed/report', { params: { format: 'html' }, responseType: 'text' }); const w = window.open('', '_blank'); if (w) { w.document.open(); w.document.write(r.data); w.document.close(); } };
  const invoiceMemberNow = async (userId) => (await http.post(`/admin/renewals/${userId}/invoice`)).data;
  const officeBearerContact = async (r) => (await http.get('/office-bearers/contact', { params: { user_id: r.user_id || '', role: r.role || '', name: r.name || '' } })).data;

  const updateSettings = async (next) => {
    const { data } = await http.put('/admin/settings', next);
    setSettings(data);
    return data;
  };

  // ---- Appearance templates ----
  const getTemplates = async () => (await http.get('/admin/templates')).data;
  const saveTemplate = async (name) => (await http.post('/admin/templates', { name })).data;
  const applyTemplate = async (templateId) => {
    const { data } = await http.post(`/admin/templates/${templateId}/apply`);
    setSettings(data);
    return data;
  };
  const deleteTemplate = async (templateId) => { await http.delete(`/admin/templates/${templateId}`); };

  // ---- Pages CMS ----
  const createPage = async (payload) => {
    const { data } = await http.post('/admin/pages', payload);
    await Promise.all([fetchPages(), fetchPermissions()]);
    return data;
  };
  const updatePage = async (pageKey, payload) => {
    const { data } = await http.put(`/admin/pages/${pageKey}`, payload);
    await Promise.all([fetchPages(), fetchPermissions()]);
    return data;
  };
  const deletePage = async (pageKey) => {
    await http.delete(`/admin/pages/${pageKey}`);
    await Promise.all([fetchPages(), fetchPermissions()]);
  };
  const editPageContent = async (pageKey, payload) => {
    const { data } = await http.put(`/pages/${pageKey}/content`, payload);
    await fetchPages();
    return data;
  };
  const convertPage = async (pageKey, payload) => {
    await http.post(`/admin/pages/${pageKey}/convert`, payload);
    await Promise.all([fetchPages(), fetchPermissions()]);
  };

  // ---- Image editing ----
  const uploadImage = async (dataUrl, contentType) => {
    const { data } = await http.post('/admin/upload', { data: dataUrl, content_type: contentType });
    return `${BACKEND_URL}${data.url}`; // data.url already starts with /api
  };
  const removeImageBackground = async (src, tolerance, save = false) => {
    const { data } = await http.post('/admin/images/remove-background', { src, tolerance, save }, { timeout: 60000 });
    return data.url ? { ...data, url: `${BACKEND_URL}${data.url}` } : data;
  };
  const setImageOverride = async (key, url, opts = {}) => {
    const next = { ...settings, images: { ...(settings.images || {}), [key]: url } };
    if (opts.clearRotation && settings.imageRotations && settings.imageRotations[key]) {
      const nr = { ...settings.imageRotations };
      delete nr[key];
      next.imageRotations = nr;
    }
    return updateSettings(next);
  };
  const clearImageOverride = async (key) => {
    const nextImages = { ...(settings.images || {}) };
    delete nextImages[key];
    return updateSettings({ ...settings, images: nextImages });
  };
  const setImageRotation = async (key, config) => {
    const next = { ...(settings.imageRotations || {}), [key]: config };
    return updateSettings({ ...settings, imageRotations: next });
  };
  const clearImageRotation = async (key) => {
    const next = { ...(settings.imageRotations || {}) };
    delete next[key];
    return updateSettings({ ...settings, imageRotations: next });
  };
  const setImageLayout = async (key, layout) => {
    const next = { ...(settings.imageLayouts || {}), [key]: layout };
    return updateSettings({ ...settings, imageLayouts: next });
  };
  const clearImageLayout = async (key) => {
    const next = { ...(settings.imageLayouts || {}) };
    delete next[key];
    return updateSettings({ ...settings, imageLayouts: next });
  };
  const getPhotoTags = async () => (await http.get('/admin/photo-tags')).data;
  const getPhotosByTag = async (tag) => (await http.get(`/photos/by-tag/${encodeURIComponent(tag)}`)).data;
  const getMemberLocations = async () => (await http.get('/admin/members/locations')).data;
  const getTags = async () => (await http.get('/tags')).data;
  const getAdminTags = async () => (await http.get('/admin/tags')).data;
  const addTag = async (name) => (await http.post('/admin/tags', { name })).data;
  const renameTag = async (oldName, newName) => (await http.put('/admin/tags/rename', { old: oldName, new: newName })).data;
  const mergeTags = async (sources, target) => (await http.post('/admin/tags/merge', { sources, target })).data;
  const deleteTag = async (name) => (await http.delete(`/admin/tags/${encodeURIComponent(name)}`)).data;
  const applyPhotoMeta = async (photo_ids, meta) => (await http.post('/my/photos/apply_meta', { photo_ids, ...meta })).data;
  const analyzePhoto = async (photo_id) => (await http.post(`/my/photos/${photo_id}/analyze`)).data;
  const getTagsMeta = async () => (await http.get('/tags/meta')).data;
  const setTagColor = async (name, color) => (await http.put('/admin/tags/color', { name, color })).data;
  const getShopProducts = async () => (await http.get('/shop/products')).data;
  const likeProduct = async (id) => (await http.post(`/shop/products/${id}/like`, {})).data;
  const recordProductView = async (id) => { try { await http.post(`/shop/products/${id}/view`, {}); } catch (e) {} };
  const shopLikesReport = async () => (await http.get('/shop/likes-report')).data;
  const adminGetProducts = async () => (await http.get('/admin/shop/products')).data;
  const adminCreateProduct = async (p) => (await http.post('/admin/shop/products', p)).data;
  const adminUpdateProduct = async (id, p) => (await http.put(`/admin/shop/products/${id}`, p)).data;
  const adminDeleteProduct = async (id) => (await http.delete(`/admin/shop/products/${id}`)).data;
  const adminGetOrders = async () => (await http.get('/admin/shop/orders')).data;
  const shopCheckout = async (body) => (await http.post('/shop/checkout', { ...body, origin_url: window.location.origin })).data;
  const shopOrderStatus = async (sid) => (await http.get(`/shop/orders/status/${sid}`)).data;
  const submitContact = async (body) => (await http.post('/contact', body)).data;
  const getApplicationInfo = async () => (await http.get('/membership/application-info')).data;
  const submitApplication = async (body) => (await http.post('/membership/applications', body)).data;
  const getApplicationDraft = async () => (await http.get('/membership/applications/draft')).data;
  const saveApplicationDraft = async (form) => (await http.put('/membership/applications/draft', { form })).data;
  const deleteApplicationDraft = async () => (await http.delete('/membership/applications/draft')).data;
  const getApplicationPrefill = async (code) => (await http.get(`/membership/applications/prefill/${code}`)).data;
  const commsRenewalsDue = async () => (await http.get('/admin/comms/renewals-due')).data;
  const commsCampaigns = async () => (await http.get('/admin/comms/campaigns')).data;
  const commsCampaignFollowUp = async (id, force = false) => (await http.post(`/admin/comms/campaigns/${id}/follow-up${force ? '?force=true' : ''}`)).data;
  const getSmsConfig = async () => (await http.get('/admin/sms/config')).data;
  const updateSmsConfig = async (body) => (await http.put('/admin/sms/config', body)).data;
  const sendTestSms = async (to) => (await http.post('/admin/sms/test', { to })).data;
  const commsMergeFields = async () => (await http.get('/admin/comms/merge-fields')).data;
  const commsPreview = async (body) => (await http.post('/admin/comms/preview', body)).data;
  const commsSend = async (body) => (await http.post('/admin/comms/send', body)).data;
  const commsLettersPdf = async (body, name) => { const r = await http.post('/admin/comms/letters.pdf', body, { responseType: 'blob' }); saveBlob(r.data, name || 'letters.pdf'); };
  const commsHistory = async () => (await http.get('/admin/comms/history')).data;
  const memberCorrespondence = async (userId) => (await http.get(`/admin/members/${userId}/correspondence`)).data;
  const getCorrespondenceItem = async (id) => (await http.get(`/admin/correspondence/${id}`)).data;
  const validateEmail = async (email) => (await http.post('/email/validate', { email })).data;
  const emailHealthCheck = async (userIds = []) => { const r = (await http.post('/admin/email-health/check', { user_ids: userIds })).data; refreshUsers(); return r; };
  const getEmailHealthSummary = async () => (await http.get('/admin/email-health/summary')).data;
  const updateEmailHealthSettings = async (patch) => (await http.put('/admin/email-health/settings', patch)).data;
  const sendEmailConfirmLinks = async (userIds) => { const r = (await http.post('/admin/email-health/verify-request', { user_ids: userIds })).data; refreshUsers(); return r; };
  const confirmEmail = async (token) => (await http.get(`/email/verify/${token}`)).data;
  const shopAnalytics = async (from, to) => (await http.get('/admin/shop/analytics', { params: { ...(from ? { from } : {}), ...(to ? { to } : {}) } })).data;
  const resolveShortLink = async (code) => (await http.get(`/short-links/${code}`)).data;
  const adminApplications = async (status) => (await http.get('/admin/membership/applications', { params: status ? { status } : {} })).data;
  const adminUpdateApplication = async (id, fields) => (await http.patch(`/admin/membership/applications/${id}`, fields)).data;
  const unappliedPayers = async () => (await http.get('/admin/membership/applications/unapplied-payers')).data;
  const inviteToApply = async (userIds) => (await http.post('/admin/membership/applications/invite', { user_ids: userIds })).data;
  const applicationWorkflow = async (id, step, body) => (await http.post(`/admin/membership/applications/${id}/${step}`, body || {})).data;
  const adminApplicationPdf = async (id, name) => { const r = await http.get(`/admin/membership/applications/${id}/pdf`, { responseType: 'blob' }); saveBlob(r.data, name || 'application.pdf'); };
  const linksAudit = async () => (await http.get('/admin/links/audit')).data;
  const linksFix = async () => (await http.post('/admin/links/fix')).data;
  const linksCheck = async () => (await http.get('/admin/links/check', { timeout: 180000 })).data;
  const getMyDetails = useCallback(async () => (await http.get('/members/me/details')).data, []);
  const updateMyDetails = async (body) => (await http.put('/members/me/details', body)).data;
  const adminMemberApplicationForm = async (userId) => (await http.get(`/admin/users/${userId}/application-form`)).data;
  const adminMemberApplicationPdf = async (userId, body, name) => { const r = await http.post(`/admin/users/${userId}/application-form.pdf`, body, { responseType: 'blob' }); saveBlob(r.data, name || 'application.pdf'); };
  const getShopProduct = async (id) => (await http.get(`/shop/products/${id}`)).data;
  const favouriteProduct = async (id) => (await http.post(`/shop/products/${id}/favourite`, {})).data;

  // ---- Subgroups ----
  const getSubgroups = async () => (await http.get('/subgroups')).data;
  const getSubgroup = async (slug) => (await http.get(`/subgroups/${slug}`)).data;
  const getSubgroupPage = async (slug, key) => (await http.get(`/subgroups/${slug}/pages/${key}`)).data;
  const getMySubgroups = async () => (await http.get('/my/subgroups')).data;
  const joinSubgroup = async (slug) => (await http.post(`/subgroups/${slug}/join`, {})).data;
  const updateSubgroup = async (slug, body) => (await http.put(`/subgroups/${slug}`, body)).data;
  const sgAdminListPages = async (slug) => (await http.get(`/subgroups/${slug}/manage/pages`)).data;
  const sgCreatePage = async (slug, body) => (await http.post(`/subgroups/${slug}/pages`, body)).data;
  const sgUpdatePage = async (slug, key, body) => (await http.put(`/subgroups/${slug}/pages/${key}`, body)).data;
  const sgDeletePage = async (slug, key) => (await http.delete(`/subgroups/${slug}/pages/${key}`)).data;
  const sgListMembers = async (slug) => (await http.get(`/subgroups/${slug}/members`)).data;
  const sgAddMember = async (slug, body) => (await http.post(`/subgroups/${slug}/members`, body)).data;
  const sgUpdateMember = async (slug, uid, body) => (await http.put(`/subgroups/${slug}/members/${uid}`, body)).data;
  const sgRecordPayment = async (slug, uid) => (await http.post(`/subgroups/${slug}/members/${uid}/record-payment`, {})).data;
  const sgRemoveMember = async (slug, uid) => (await http.delete(`/subgroups/${slug}/members/${uid}`)).data;
  const adminGetSubgroups = async () => (await http.get('/admin/subgroups')).data;
  const adminCreateSubgroup = async (body) => (await http.post('/admin/subgroups', body)).data;
  const adminAssignGroupAdmin = async (slug, email) => (await http.post(`/admin/subgroups/${slug}/group-admins`, { email })).data;
  const adminDeleteSubgroup = async (slug) => (await http.delete(`/admin/subgroups/${slug}`)).data;
  const requestSubgroup = async (body) => (await http.post('/subgroups/request', body)).data;
  const createSubgroupSelf = async (body) => (await http.post('/subgroups', body)).data;
  const adminApproveSubgroup = async (slug) => (await http.post(`/admin/subgroups/${slug}/approve`)).data;
  const adminRejectSubgroup = async (slug) => (await http.post(`/admin/subgroups/${slug}/reject`)).data;
  const adminListSubgroupRequests = async () => (await http.get('/admin/subgroup-requests')).data;
  const adminUpdateSubgroupRequest = async (id, status) => (await http.patch(`/admin/subgroup-requests/${id}`, { status })).data;
  const adminDeleteSubgroupRequest = async (id) => (await http.delete(`/admin/subgroup-requests/${id}`)).data;
  const adminClearDismissedRequests = async () => (await http.post('/admin/subgroup-requests/clear-dismissed')).data;
  const checkSubgroupName = async (name) => (await http.get('/subgroup-name-check', { params: { name } })).data;
  const sgUploadImage = async (slug, dataUrl, contentType) => {
    const { data } = await http.post(`/subgroups/${slug}/upload`, { data: dataUrl, content_type: contentType });
    return `${BACKEND_URL}${data.url}`;
  };

  // ---- Articles (WYSIWYG editor) ----
  const getArticleFields = async () => (await http.get('/admin/articles/fields')).data;
  const listArticles = async () => (await http.get('/admin/articles')).data;
  const getArticle = async (id) => (await http.get(`/admin/articles/${id}`)).data;
  const createArticle = async (body) => (await http.post('/admin/articles', body)).data;
  const updateArticle = async (id, body) => (await http.put(`/admin/articles/${id}`, body)).data;
  const deleteArticle = async (id) => (await http.delete(`/admin/articles/${id}`)).data;
  const previewArticle = async (body) => (await http.post('/admin/articles/preview', body)).data;
  const exportArticleUrl = (format) => `${API}/admin/articles/export?format=${format}`;
  const exportArticle = async (body, format) => {
    const res = await http.post(`/admin/articles/export?format=${format}`, body, { responseType: 'blob' });
    return res.data;
  };
  const publishArticlePage = async (body) => (await http.post('/admin/articles/publish-page', body)).data;
  const sendArticleEmail = async (body) => (await http.post('/admin/articles/send-email', body)).data;
  const getPublicArticle = async (id) => (await http.get(`/public/article/${id}`)).data;
  const listPublicArticles = async () => (await http.get('/public/articles')).data;

  // ---- System output templates ----
  const listSystemTemplates = async () => (await http.get('/admin/system-templates')).data;
  const getSystemTemplate = async (slug) => (await http.get(`/admin/system-templates/${slug}`)).data;
  const saveSystemTemplate = async (slug, body) => (await http.put(`/admin/system-templates/${slug}`, body)).data;
  const resetSystemTemplate = async (slug) => (await http.delete(`/admin/system-templates/${slug}`)).data;
  const previewSystemTemplate = async (slug, body) => (await http.post(`/admin/system-templates/${slug}/preview`, body)).data;

  // ---- Meeting Manager ----
  const listMeetings = async () => (await http.get('/meetings')).data;
  const getMeeting = async (id) => (await http.get(`/meetings/${id}`)).data;
  const rsvpMeeting = async (id, status) => (await http.post(`/meetings/${id}/rsvp`, { status })).data;
  const setActionStatus = async (id, actionId, status) => (await http.post(`/meetings/${id}/actions/${actionId}/status`, { status })).data;
  const meetingIcsUrl = (id) => `${API}/meetings/${id}/ics`;
  const meetingsCalendarEvents = async (days = 180) => (await http.get('/meetings-calendar/events', { params: { days } })).data;
  const meetingsFeedUrl = async () => (await http.get('/meetings-calendar/feed-url')).data;
  const suggestAgendaItem = async (id, body) => (await http.post(`/meetings/${id}/agenda-suggestions`, body)).data;
  const decideAgendaSuggestion = async (id, sid, body) => (await http.post(`/admin/meetings/${id}/agenda-suggestions/${sid}`, body)).data;
  const meetingCalendarSync = async (id) => (await http.post(`/admin/meetings/${id}/calendar-sync`)).data;
  const adminListFaces = async (params) => (await http.get('/admin/faces', { params })).data;
  const adminNameFace = async (pid, fid, name) => (await http.post(`/admin/photos/${pid}/faces/${fid}`, { name })).data;
  const adminDetectFaces = async (limit = 5) => (await http.post('/admin/faces/detect', null, { params: { limit }, timeout: 180000 })).data;
  const meetingConsole = async (id) => (await http.get(`/admin/meetings/${id}/console`)).data;
  const addLiveNote = async (id, body) => (await http.post(`/admin/meetings/${id}/live-notes`, body)).data;
  const deleteLiveNote = async (id, nid) => (await http.delete(`/admin/meetings/${id}/live-notes/${nid}`)).data;
  const motionHands = async (id, mid, body) => (await http.post(`/admin/meetings/${id}/motions/${mid}/hands`, body)).data;
  const uploadRecording = async (id, blob, label) => { const fd = new FormData(); fd.append('audio', blob, 'recording.webm'); fd.append('label', label || ''); return (await http.post(`/admin/meetings/${id}/recordings`, fd, { timeout: 300000 })).data; };
  const deleteRecording = async (id, rid) => (await http.delete(`/admin/meetings/${id}/recordings/${rid}`)).data;
  const recordingAudioUrl = (id, rid) => `${API}/admin/meetings/${id}/recordings/${rid}/audio`;
  const adminHistory = async (params) => (await http.get('/admin/history', { params })).data;
  const adminHistoryExportUrl = (type) => `${API}/admin/history/export${type ? `?obj_type=${encodeURIComponent(type)}` : ''}`;
  const structureReportPdfUrl = () => `${API}/admin/reports/structure.pdf`;
  const boardFlyerPdfUrl = () => `${API}/admin/reports/flyer.pdf`;
  const flyerConfig = async () => (await http.get('/admin/reports/flyer/config')).data;
  const flyerRegenerate = async (body) => (await http.post('/admin/reports/flyer/regenerate', body, { timeout: 150000 })).data;
  // ---- Registries ----
  const regMotions = async (params) => (await http.get('/admin/motions', { params })).data;
  const regEditMotion = async (mid, moid, body) => (await http.put(`/admin/motions/${mid}/${moid}`, body)).data;
  const regPayments = async (params) => (await http.get('/admin/payments', { params })).data;
  const regEditPayment = async (sid, body) => (await http.put(`/admin/payments/${sid}`, body)).data;
  const regResendReceipt = async (sid) => (await http.post(`/admin/payments/${sid}/resend-receipt`)).data;
  const regInvoices = async (params) => (await http.get('/admin/invoices', { params })).data;
  const regCreateInvoice = async (body) => (await http.post('/admin/invoices', body)).data;
  const regUpdateInvoice = async (id, body) => (await http.put(`/admin/invoices/${id}`, body)).data;
  const regDeleteInvoice = async (id) => (await http.delete(`/admin/invoices/${id}`)).data;
  const regSendInvoice = async (id) => (await http.post(`/admin/invoices/${id}/send`)).data;
  const regRemindInvoice = async (id) => (await http.post(`/admin/invoices/${id}/remind`)).data;
  const regReminderSettings = async () => (await http.get('/admin/invoices/reminders/settings')).data;
  const regSetReminderSettings = async (body) => (await http.put('/admin/invoices/reminders/settings', body)).data;
  const regRunReminders = async () => (await http.post('/admin/invoices/reminders/run')).data;
  const adminHistoryBackfill = async () => (await http.post('/admin/history/backfill')).data;
  const regReminderPreview = async (body) => (await http.post('/admin/invoices/reminders/preview', body)).data;
  const birthdaySettings = async () => (await http.get('/admin/birthdays/settings')).data;
  const saveBirthdaySettings = async (body) => (await http.put('/admin/birthdays/settings', body)).data;
  const birthdayPreview = async (body) => (await http.post('/admin/birthdays/preview', body)).data;
  const birthdayTest = async (body) => (await http.post('/admin/birthdays/test', body)).data;
  const birthdayRun = async () => (await http.post('/admin/birthdays/run')).data;
  const birthdayUpcoming = async (days = 30) => (await http.get('/admin/birthdays/upcoming', { params: { days } })).data;
  const getOfficeBearersHistory = async () => (await http.get('/office-bearers/history')).data;
  const adminAlbumSlideshowLink = async (name) => (await http.get(`/admin/photos/albums/${encodeURIComponent(name)}/slideshow-link`)).data;
  const albumPhotoBook = async (name, body) => { const r = await http.post(`/photos/albums/${encodeURIComponent(name)}/book.pdf`, body, { responseType: 'blob', timeout: 300000 }); saveBlob(r.data, `${name} - Photo Book.pdf`); };
  // ---- Transactions ledger ----
  const tx = {
    accounts: async () => (await http.get('/admin/tx/accounts')).data,
    saveAccount: async (b) => (await http.post('/admin/tx/accounts', b)).data,
    setOpening: async (id, b) => (await http.put(`/admin/tx/accounts/${id}/opening`, b)).data,
    deleteAccount: async (id) => (await http.delete(`/admin/tx/accounts/${id}`)).data,
    removeFeed: async (id, confirm) => (await http.delete(`/admin/tx/accounts/${id}`, { params: { purge: true, confirm } })).data,
    list: async (params) => (await http.get('/admin/tx', { params })).data,
    update: async (id, b) => (await http.put(`/admin/tx/${id}`, b)).data,
    exportUrl: (params) => `${API}/admin/tx/export.csv?${new URLSearchParams(params).toString()}`,
    importCsv: async (account, file) => { const fd = new FormData(); fd.append('file', file); return (await http.post(`/admin/tx/import-csv?account=${encodeURIComponent(account)}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } })).data; },
    sync: async (src, days = 90) => (await http.post(`/admin/tx/sync/${src}?days=${days}`)).data,
    sources: async () => (await http.get('/admin/tx/sources')).data,
    paypalConfig: async (b) => (await http.put('/admin/tx/sources/paypal', b)).data,
    rules: async () => (await http.get('/admin/tx/rules')).data,
    saveRule: async (b) => (await http.post('/admin/tx/rules', b)).data,
    deleteRule: async (id) => (await http.delete(`/admin/tx/rules/${id}`)).data,
    categorise: async (force = false) => (await http.post(`/admin/tx/categorise?force=${force}`)).data,
    dupScan: async () => (await http.post('/admin/tx/duplicates/scan')).data,
    dups: async () => (await http.get('/admin/tx/duplicates')).data,
    dupResolve: async (id, b) => (await http.post(`/admin/tx/duplicates/${id}/resolve`, b)).data,
    restore: async (id) => (await http.post(`/admin/tx/${id}/restore`)).data,
    remove: async (id) => (await http.delete(`/admin/tx/${id}`)).data,
    addManual: async (b) => (await http.post('/admin/tx/manual', b)).data,
    statement: async (params) => (await http.get('/admin/tx/statement', { params })).data,
    statementCsvUrl: (params) => `${API}/admin/tx/statement.csv?${new URLSearchParams(Object.fromEntries(Object.entries(params).filter(([, v]) => v))).toString()}`,
    reconcileBulk: async () => (await http.post('/admin/tx/reconcile-bulk')).data,
    linkBulk: async (id, ids) => (await http.post(`/admin/tx/${id}/link-bulk`, { child_ids: ids })).data,
    children: async (id) => (await http.get(`/admin/tx/${id}/children`)).data,
    events: async () => (await http.get('/admin/tx/events')).data,
    report: async (from, to) => (await http.get('/admin/tx/report', { params: { from_date: from, to_date: to } })).data,
    reportPdfUrl: (from, to) => `${API}/admin/tx/report.pdf?from_date=${from}&to_date=${to}`,
  };
  // ---- Raffles ----
  const getRaffles = async () => (await http.get('/raffles')).data;
  const getRaffle = async (id) => (await http.get(`/raffles/${id}`)).data;
  const raffleCheckout = async (id, body) => (await http.post(`/raffles/${id}/checkout`, { ...body, origin_url: window.location.origin })).data;
  const raffleBookOrder = async (id, body) => (await http.post(`/raffles/${id}/book-order`, { ...body, origin_url: window.location.origin })).data;
  const myRaffleBooks = async () => (await http.get('/raffle-agent/my-books')).data;
  const raffleStub = async (code) => (await http.get(`/raffle-agent/stub/${code}`)).data;
  const raffleStubPresent = async (code, body) => (await http.post(`/raffle-agent/stub/${code}/present`, body)).data;
  const raffleTicketScan = async (code) => (await http.get(`/raffle-agent/ticket/${code}`)).data;
  const raffleTicketContact = async (code, body) => (await http.post(`/raffle-agent/ticket/${code}/contact`, body)).data;
  const adminRaffleAgentSearch = async (q) => (await http.get('/admin/raffles/agents/search', { params: { q } })).data;
  const adminRaffleAgents = async (id) => (await http.get(`/admin/raffles/${id}/agents`)).data;
  const adminRaffleStatement = async (id, bookId, body) => (await http.post(`/admin/raffles/${id}/books/${bookId}/statement`, body)).data;
  const adminRaffleSettle = async (id, bookId, body) => (await http.post(`/admin/raffles/${id}/books/${bookId}/settle`, body)).data;
  const adminRaffleUnpresented = async (id, bookId) => (await http.post(`/admin/raffles/${id}/books/${bookId}/unpresented`)).data;
  const raffleStatementUrl = (id, bookId) => `${API}/admin/raffles/${id}/books/${bookId}/statement.pdf`;
  const raffleTicketsUrl = (path) => `${API}${path}`;
  const raffleClaimGet = async (id, t) => (await http.get(`/raffles/${id}/claim`, { params: { t } })).data;
  const raffleClaimPost = async (id, body) => (await http.post(`/raffles/${id}/claim`, body)).data;
  const adminRaffles = async () => (await http.get('/admin/raffles')).data;
  const adminRaffleLinkBase = async () => (await http.get('/admin/raffles/link-base')).data;
  const adminCreateRaffle = async (body) => (await http.post('/admin/raffles', body)).data;
  const adminUpdateRaffle = async (id, body) => (await http.put(`/admin/raffles/${id}`, body)).data;
  const adminDeleteRaffle = async (id) => (await http.delete(`/admin/raffles/${id}`)).data;
  const adminRaffleAction = async (id, action, body = {}) => (await http.post(`/admin/raffles/${id}/${action}`, body)).data;
  const adminRaffleTickets = async (id) => (await http.get(`/admin/raffles/${id}/tickets`)).data;
  const adminRaffleTicketComment = async (id, tid, body) => (await http.put(`/admin/raffles/${id}/tickets/${tid}/comment`, typeof body === 'string' ? { comment: body } : body)).data;
  const adminRaffleCashSale = async (id, body) => (await http.post(`/admin/raffles/${id}/tickets/cash`, body)).data;
  const adminRaffleBooks = async (id) => (await http.get(`/admin/raffles/${id}/tickets/books`)).data;
  const adminRaffleCreateBook = async (id, body) => (await http.post(`/admin/raffles/${id}/tickets/book`, body)).data;
  const raffleExampleUrl = (id, kind) => `${API}/admin/raffles/${id}/examples/${kind}.pdf`;
  const adminRaffleExamplesRegenerate = async (id) => (await http.post(`/admin/raffles/${id}/examples/regenerate`)).data;
  const adminRaffleVoucher = async (id, pid, body) => (await http.post(`/admin/raffles/${id}/prizes/${pid}/voucher`, body)).data;
  const adminRafflePrize = async (id, pid, action, body) => (await http.post(`/admin/raffles/${id}/prizes/${pid}/${action}`, body)).data;
  const adminRaffleReportUrl = (id, kind) => `${API}/admin/raffles/${id}/reports/${kind}.pdf`;
  const rafflePosterUrl = (id, admin = false) => `${API}/${admin ? 'admin/' : ''}raffles/${id}/poster.pdf`;
  // ---- Photo-book sales ----
  const bookForAlbum = async (name) => (await http.get(`/photo-books/for-album/${encodeURIComponent(name)}`)).data;
  const getPhotoBook = async (id, token) => (await http.get(`/photo-books/${id}`, { params: token ? { t: token } : {} })).data;
  const photoBookDownloadUrl = (id, token) => `${API}/photo-books/${id}/download${token ? `?t=${encodeURIComponent(token)}` : ''}`;
  const adminPhotoBooks = async () => (await http.get('/admin/photo-books')).data;
  const adminPhotoBookForAlbum = async (name) => (await http.get(`/admin/photo-books/by-album/${encodeURIComponent(name)}`)).data;
  const adminSavePhotoBook = async (body) => (await http.post('/admin/photo-books', body, { timeout: 300000 })).data;
  const adminRebuildPhotoBook = async (id) => (await http.post(`/admin/photo-books/${id}/rebuild`, {}, { timeout: 300000 })).data;
  const adminDeletePhotoBook = async (id) => (await http.delete(`/admin/photo-books/${id}`)).data;
  const adminPhotoBookFileUrl = (id) => `${API}/admin/photo-books/${id}/file.pdf`;
  const adminPrintOrders = async () => (await http.get('/admin/print-orders')).data;
  const adminUpdatePrintOrder = async (id, body) => (await http.put(`/admin/print-orders/${id}`, body)).data;
  const adminPrintQuote = async (id) => (await http.post(`/admin/print-orders/${id}/quote`, {}, { timeout: 60000 })).data;
  const adminSendToLulu = async (id) => (await http.post(`/admin/print-orders/${id}/send-to-lulu`, {}, { timeout: 180000 })).data;
  const adminRefreshLulu = async (id) => (await http.post(`/admin/print-orders/${id}/refresh`, {}, { timeout: 60000 })).data;
  const luluConfig = async () => (await http.get('/admin/lulu/config')).data;
  const saveLuluConfig = async (body) => (await http.put('/admin/lulu/config', body)).data;
  const luluTest = async () => (await http.post('/admin/lulu/test', {}, { timeout: 60000 })).data;
  // ---- Memorabilia ----
  const memoList = async () => (await http.get('/memorabilia')).data;
  const memoMeta = async () => (await http.get('/memorabilia/meta')).data;
  const memoCreate = async (body) => (await http.post('/admin/memorabilia', body)).data;
  const memoUpdate = async (id, body) => (await http.put(`/admin/memorabilia/${id}`, body)).data;
  const memoDelete = async (id) => (await http.delete(`/admin/memorabilia/${id}`)).data;
  const memoSetPhotos = async (id, photo_ids) => (await http.put(`/admin/memorabilia/${id}/photos`, { photo_ids })).data;
  const memoUpload = async (id, data, title) => (await http.post(`/admin/memorabilia/${id}/upload`, { data, title }, { timeout: 120000 })).data;
  const memoUnlinked = async () => (await http.get('/admin/memorabilia/unlinked-photos')).data;
  const memoAlbumPhotos = async () => (await http.get('/admin/memorabilia/album-photos')).data;
  const memoFromPhoto = async (photoId) => (await http.post(`/admin/memorabilia/from-photo/${photoId}`)).data;
  const memoCopyAi = async (id, body) => (await http.post(`/admin/memorabilia/${id}/copy-ai`, body)).data;
  const memoImport = async (body) => (await http.post('/admin/memorabilia/import', body, { timeout: 120000 })).data;
  const memoImportStatus = async () => (await http.get('/admin/memorabilia/import/status')).data;
  const memoRetryImages = async () => (await http.post('/admin/memorabilia/import/retry-images')).data;
  const memoStopImport = async () => (await http.post('/admin/memorabilia/import/stop')).data;
  const memoAlbum = async (ids, name) => (await http.post('/admin/memorabilia/album', { ids, name })).data;
  const memoAiDraft = async (id, fields) => (await http.post(`/admin/memorabilia/${id}/ai-draft`, { fields }, { timeout: 120000 })).data;
  const memoBundledInfo = async () => (await http.get('/admin/memorabilia/import/bundled')).data;
  const memoImportBundled = async (body) => (await http.post('/admin/memorabilia/import/bundled', body, { timeout: 120000 })).data;
  const memoAiBulk = async (ids, fields) => (await http.post('/admin/memorabilia/ai-bulk', { ids, fields })).data;
  const memoAiBulkStatus = async () => (await http.get('/admin/memorabilia/ai-bulk/status')).data;
  const memoAiDrafts = async () => (await http.get('/admin/memorabilia/ai-drafts')).data;
  const memoAiDraftApply = async (id, fields, mode) => (await http.post(`/admin/memorabilia/ai-drafts/${id}/apply`, { fields, mode })).data;
  const memoAiDraftDismiss = async (id) => (await http.post(`/admin/memorabilia/ai-drafts/${id}/dismiss`)).data;
  const memoPrint = async (ids, format) => { const r = await http.post('/memorabilia/print.pdf', { ids, format }, { responseType: 'blob', timeout: 300000 }); saveBlob(r.data, format === 'amaga' ? 'AMaGA Catalogue Records.pdf' : 'RAE Tas Collection.pdf'); };
  const regInvoicePdfUrl = (id) => `${API}/admin/invoices/${id}/pdf`;
  const regEmails = async (params) => (await http.get('/admin/emails', { params })).data;
  const regEmailDetail = async (id) => (await http.get(`/admin/emails/${id}`)).data;
  const regDeleteEmail = async (id) => (await http.delete(`/admin/emails/${id}`)).data;
  const regResendEmail = async (id) => (await http.post(`/admin/emails/${id}/resend`)).data;
  const meetingAgendaPdfUrl = (id) => `${API}/admin/meetings/${id}/agenda.pdf`;
  const meetingMinutesPdfUrl = (id) => `${API}/admin/meetings/${id}/minutes.pdf`;
  const adminListMeetings = async () => (await http.get('/admin/meetings')).data;
  const adminCreateMeeting = async (body) => (await http.post('/admin/meetings', body)).data;
  const adminUpdateMeeting = async (id, body) => (await http.put(`/admin/meetings/${id}`, body)).data;
  const adminDeleteMeeting = async (id) => (await http.delete(`/admin/meetings/${id}`)).data;
  const adminMarkAttendance = async (id, email, present) => (await http.post(`/admin/meetings/${id}/attendance`, { email, present })).data;
  const emailMeetingMinutes = async (id) => (await http.post(`/admin/meetings/${id}/email-minutes`)).data;
  const aiMinutesProse = async (id) => (await http.post(`/admin/meetings/${id}/minutes-prose`)).data;
  const saveMeetingMinutes = async (id, html) => (await http.post(`/admin/meetings/${id}/minutes`, { html })).data;
  const governanceMotions = async () => (await http.get('/governance/motions')).data;
  const motionLifecycle = async (id, motionId, body) => (await http.post(`/admin/meetings/${id}/motions/${motionId}/lifecycle`, body)).data;
  const govListDocs = async () => (await http.get('/governance/documents')).data;
  const govAdminListDocs = async () => (await http.get('/admin/governance/documents')).data;
  const govCreateDoc = async (body) => (await http.post('/admin/governance/documents', body)).data;
  const govExtractDoc = async (id) => (await http.post(`/admin/governance/documents/${id}/extract`)).data;
  const govRematchDoc = async (id) => (await http.post(`/admin/governance/documents/${id}/rematch`)).data;
  const govFillContent = async (id, overwrite = false) => (await http.post(`/admin/governance/documents/${id}/fill-content`, null, { params: { overwrite } })).data;
  const govDeleteDoc = async (id) => (await http.delete(`/admin/governance/documents/${id}`)).data;
  const govUpdatePolicy = async (docId, policyId, body) => (await http.put(`/admin/governance/documents/${docId}/policies/${policyId}`, body)).data;
  const govCarriedMotions = async () => (await http.get('/admin/governance/carried-motions')).data;
  const sendEmailBallots = async (id, motionId) => (await http.post(`/admin/meetings/${id}/motions/${motionId}/send-ballots`)).data;
  const treasurerReportPdfUrl = (status = 'confirmed') => `${API}/admin/basiq/report.pdf?status=${status}`;
  const adminCreateMotion = async (id, body) => (await http.post(`/admin/meetings/${id}/motions`, body)).data;
  const adminCloseMotion = async (id, motionId) => (await http.post(`/admin/meetings/${id}/motions/${motionId}/close`)).data;
  const adminDeleteMotion = async (id, motionId) => (await http.delete(`/admin/meetings/${id}/motions/${motionId}`)).data;
  const castVote = async (id, motionId, choice) => (await http.post(`/meetings/${id}/motions/${motionId}/vote`, { choice })).data;

  // ---- Admin media library (gallery picker) ----
  const adminMediaList = async (tags = [], match = 'any') => (await http.get('/admin/media', { params: { tags: tags.join(','), match } })).data;
  const adminMediaTags = async () => (await http.get('/admin/media/tags')).data;
  const adminMediaUpload = async (dataUrl) => (await http.post('/admin/media', { data: dataUrl, content_type: 'image/jpeg' })).data;
  const adminMediaUpdate = async (id, body) => (await http.patch(`/admin/media/${id}`, body)).data;
  const adminMediaRegen = async (id) => (await http.post(`/admin/media/${id}/regenerate`, {})).data;
  const adminMediaUsage = async (id) => (await http.get(`/admin/media/${id}/usage`)).data;
  const adminMediaDelete = async (id, force = false) => (await http.delete(`/admin/media/${id}`, { params: { force } })).data;
  const adminMediaByImage = async (imageId) => (await http.get(`/admin/media/by-image/${imageId}`)).data;
  const saveBlob = (blob, name) => { const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = name; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(a.href), 2000); };
  const dispositionName = (r, fallback) => { const m = /filename="?([^";]+)"?/.exec(r.headers['content-disposition'] || ''); return (m && m[1]) || fallback; };
  const adminMediaDownload = async (id) => { const r = await http.get(`/admin/media/${id}/download`, { responseType: 'blob' }); saveBlob(r.data, dispositionName(r, 'image.jpg')); };
  const adminMediaDownloadZip = async (ids) => { const r = await http.post('/admin/media/download-zip', { ids }, { responseType: 'blob' }); saveBlob(r.data, 'gallery-images.zip'); };
  const mediaAbsUrl = (url) => (url && url.startsWith('/') ? `${BACKEND_URL}${url}` : url);

  // ---- Editable text (admin right-click) ----
  const setContentOverride = async (key, value) => {
    const nextContent = { ...(settings.content || {}), [key]: value };
    return updateSettings({ ...settings, content: nextContent });
  };
  const clearContentOverride = async (key) => {
    const nextContent = { ...(settings.content || {}) };
    delete nextContent[key];
    return updateSettings({ ...settings, content: nextContent });
  };

  // Preview mode only applies once auth confirms the user is an admin — the raw
  // ?viewAs query string is never trusted for a logged-out / non-admin visitor.
  const previewClass = (!previewCleared && user && user.memberClass === 'admin') ? requestedPreview : null;
  const currentClass = previewClass || (user ? user.memberClass : 'public');
  const isAdmin = !!user && user.memberClass === 'admin' && !previewClass;
  // Delegated admins (site-admin / group-admin, or any class granted an admin
  // section) can reach the Admin area for the sections their role is granted.
  const isStaff = !!user && !previewClass && ADMIN_SECTIONS.some((s) => (permissions[s.perm] || []).includes(currentClass));
  const isPreview = !!previewClass;
  const canAccess = (pageKey) => (permissions[pageKey] || []).includes(currentClass);
  const canEditPage = (page) => {
    if (!user || !page) return false;
    if (typeof page.can_edit === 'boolean') return page.can_edit;
    if (isAdmin) return true;
    if (canAccess('admin-pages')) return true;
    if ((page.edit_classes || []).includes(user.memberClass)) return true;
    const email = (user.email || '').toLowerCase();
    return (page.edit_users || []).some((e) => (e || '').toLowerCase() === email);
  };

  const startPreview = (cls) => {
    if (!cls || cls === 'admin') return;
    window.open(`${window.location.origin}/?viewAs=${cls}`, '_blank', 'noopener');
  };
  const exitPreview = () => {
    setPreviewCleared(true);
    try { window.close(); } catch (e) { /* ignore */ }
    window.location.href = '/';
  };

  // ---- Member classes (built-ins + admin-added custom classes) ----
  const memberClasses = (settings && Array.isArray(settings.memberClasses) && settings.memberClasses.length)
    ? settings.memberClasses
    : DEFAULT_MEMBER_CLASSES;
  const classLabel = (key) => {
    const found = memberClasses.find((c) => c.key === key);
    return found ? found.label : (CLASS_LABELS[key] || key);
  };
  const addMemberClass = async (label) => {
    const { data } = await http.post('/admin/member-classes', { label });
    setSettings((s) => ({ ...s, memberClasses: data }));
    return data;
  };
  const renameMemberClass = async (key, label) => {
    const { data } = await http.put(`/admin/member-classes/${key}`, { label });
    setSettings((s) => ({ ...s, memberClasses: data }));
    return data;
  };
  const deleteMemberClass = async (key) => {
    const { data } = await http.delete(`/admin/member-classes/${key}`);
    setSettings((s) => ({ ...s, memberClasses: data }));
    return data;
  };

  // ---- Payments ----
  const getCatalog = async () => (await http.get('/payments/catalog')).data;
  const checkout = async (lookupKey) => {
    const { data } = await http.post('/payments/checkout', {
      lookup_key: lookupKey,
      quantity: 1,
      origin_url: window.location.origin,
      user_id: user ? user.user_id : null,
    });
    return data.checkout_url;
  };
  const getPaymentStatus = async (sessionId) => (await http.get(`/payments/status/${sessionId}`)).data;
  const getMyPayments = async () => (await http.get('/payments/my')).data;
  const getMyInvoices = async () => (await http.get('/me/invoices')).data;
  const payInvoice = async (id, t) => (await http.post(`/invoices/${id}/pay`, { t, origin_url: window.location.origin })).data;

  // ---- Google Workspace ----
  const getGoogleConfig = async () => (await http.get('/admin/google/config')).data;
  const updateGoogleConfig = async (cfg) => (await http.put('/admin/google/config', cfg)).data;
  const testGoogle = async () => (await http.post('/admin/google/test')).data;
  const getGoogleCalendar = async (days = 120) => (await http.get(`/google/calendar?days=${days}`)).data;
  const getGoogleDocuments = async () => (await http.get('/google/documents')).data;
  const createGoogleEvent = async (payload) => (await http.post('/admin/google/events', payload)).data;
  const previewGoogleSheet = async (rows = 8) => (await http.get(`/admin/google/sheet/preview?rows=${rows}`)).data;
  const getGoogleSheetTabs = async () => (await http.get('/admin/google/sheet/tabs')).data;
  const syncMembersFromSheet = async () => (await http.post('/admin/google/sheet/sync-members')).data;
  const previewSheetSync = async () => (await http.post('/admin/google/sheet/sync-preview')).data;
  const getSheetSyncLog = async (limit = 10) => (await http.get(`/admin/google/sheet/sync-log?limit=${limit}`)).data;
  const resetSheetSyncState = async () => (await http.post('/admin/google/sheet/reset-sync-state')).data;
  const getSheetSyncLogDetail = async (id) => (await http.get(`/admin/google/sheet/sync-log/${id}`)).data;
  const sendTestSyncAlert = async () => (await http.post('/admin/google/sheet/send-test-alert')).data;
  const testGoogleContacts = async () => (await http.post('/admin/google/contacts/test')).data;
  const previewContactsSync = async () => (await http.post('/admin/google/contacts/sync-preview')).data;
  const runContactsSync = async () => (await http.post('/admin/google/contacts/sync')).data;
  const getContactsSyncLog = async (limit = 10) => (await http.get(`/admin/google/contacts/sync-log?limit=${limit}`)).data;
  const getContactsSyncLogDetail = async (id) => (await http.get(`/admin/google/contacts/sync-log/${id}`)).data;
  const resetContactsSyncState = async () => (await http.post('/admin/google/contacts/reset-sync-state')).data;

  const value = {
    http,
    user, users, permissions, settings, customPages, loaded, currentClass,
    org, getOrg, updateOrg,
    previewClass, isPreview, isAdmin, isStaff, startPreview, exitPreview,
    memberClasses, classLabel, addMemberClass, renameMemberClass, deleteMemberClass,
    loginWithGoogle, processSessionId, logout,
    login, register, forgotPassword, resetPassword,
    setUserPassword, getEmailSettings, updateEmailSettings,
    sendTestEmail, getHasPassword, getMyActivity, setMyPassword,
    getAnalyticsSummary, getAnalyticsLog, analyticsExportUrl, getMemberActivity,
    getWeeklyDigest, updateWeeklyDigest, sendWeeklyDigestNow,
    listEvents, registerEvent, adminListEvents, adminSaveEvent, adminDeleteEvent, adminEventRegs,
    uploadGalleryPhoto, listMyPhotos, listMyAlbums, updateMyPhoto, deleteMyPhoto,
    getPhotoGallery, getMemberPhotos, adminListPhotos, moderatePhoto,
    adminUpdatePhoto, adminDeletePhoto, adminReplacePhotoImage, adminBulkPhotos, adminPhotoReviewDigestNow, adminPhotoAlbums, adminAlbums, adminCreateAlbum, adminUpdateAlbum, adminDeleteAlbum, adminAlbumPhotos, adminAlbumAddPhotos, adminAlbumRemovePhotos, adminAlbumReorder, adminAlbumUpload, getAppointmentRoles, getOfficeBearers, adminAiDescribePhoto, adminFaceSuggest, adminPhotoPeople,
    adminEventPhotos, adminUploadEventPhoto, adminAttachEventPhotos, adminReorderEventPhotos, listPhotoAlbums, getPhotoAlbum, adminSetAlbumMeta, adminAlbumShareLink, listPhotoComments, addPhotoComment, deletePhotoComment, dataTableSources, dataTableRows, getTagsUsed, actionMembers, createAction, myActions, listActions, actionsBySource, patchAction, delegateAction, deleteAction, bulkActions, requestDetailsUpdate, confirmMyDetails, getMyLog, getDigestPref, setDigestPref, previewDigest,
    updatePermissions, updateSettings, refreshUsers, updateUserClass, canAccess, canEditPage,
    fetchPages, createPage, updatePage, deletePage, convertPage, editPageContent,
    uploadImage, removeImageBackground, setImageOverride, clearImageOverride,
    setImageRotation, clearImageRotation, setImageLayout, clearImageLayout, getPhotoTags, getPhotosByTag, getMemberLocations,
    getTags, getAdminTags, addTag, renameTag, mergeTags, deleteTag, applyPhotoMeta,
    analyzePhoto, getTagsMeta, setTagColor,
    tx, getRaffles, getRaffle, raffleCheckout, raffleTicketsUrl, raffleClaimGet, raffleClaimPost, adminRaffles, adminRaffleLinkBase, adminCreateRaffle, adminCreateContact, adminDeleteContact, adminInviteContact, adminContactMessage, adminContactMessages, adminContactMessageHandled, membershipSettings, saveMembershipSettings, setMemberExpiry, officeBearerContact, lapsedMembers, reinstateMember, renewalApprovals, decideRenewalApproval, runLapseSweep, previewLapseSweep, previewGraceWarnings, runGraceWarnings, lapsedReportPdf, lapsedReportPrint, renewalInvoiceSettings, saveRenewalInvoiceSettings, runRenewalInvoices, previewRenewalInvoice, invoiceMemberNow, welcomePending, saveWelcomeSettings, welcomeRun, welcomePreview, welcomeSend, welcomeSkip, emailQueueList, emailQueueCount, emailQueueItem, emailQueueEdit, emailQueueRelease, emailQueueAbort, emailQueueRefreshContact, emailQueueBulk, emailQueueDelete, emailQueueSettings, saveEmailQueueSettings, emailQueueSummaryRun, emailQueueTemplateProposal, emailQueueApplyTemplate, emailQueueAutoReleaseRun, adminUpdateRaffle, adminDeleteRaffle, adminRaffleAction, adminRaffleTickets, adminRaffleTicketComment, adminRaffleCashSale, adminRaffleBooks, adminRaffleCreateBook, raffleExampleUrl, adminRaffleExamplesRegenerate, adminRaffleVoucher, raffleBookOrder, myRaffleBooks, raffleStub, raffleStubPresent, raffleTicketScan, raffleTicketContact, adminRaffleAgentSearch, adminRaffleAgents, adminRaffleStatement, adminRaffleSettle, adminRaffleUnpresented, raffleStatementUrl, adminRafflePrize, adminRaffleReportUrl, rafflePosterUrl,
    getShopProducts, adminGetProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct, adminGetOrders, shopCheckout, shopOrderStatus, submitContact, likeProduct, shopLikesReport, recordProductView, getApplicationInfo, submitApplication, adminApplications, adminUpdateApplication, adminApplicationPdf, adminMemberApplicationForm, adminMemberApplicationPdf, getMyDetails, updateMyDetails, linksAudit, linksFix, linksCheck, applicationWorkflow, unappliedPayers, inviteToApply, getShopProduct, favouriteProduct,
    getApplicationDraft, saveApplicationDraft, deleteApplicationDraft, commsMergeFields, commsPreview, commsSend, commsLettersPdf, commsHistory, memberCorrespondence, getCorrespondenceItem, validateEmail, emailHealthCheck, getEmailHealthSummary, updateEmailHealthSettings, sendEmailConfirmLinks, confirmEmail, shopAnalytics, resolveShortLink,
    getApplicationPrefill, commsRenewalsDue, commsCampaigns, commsCampaignFollowUp, getSmsConfig, updateSmsConfig, sendTestSms,
    getSubgroups, getSubgroup, getSubgroupPage, getMySubgroups, joinSubgroup, updateSubgroup,
    sgAdminListPages, sgCreatePage, sgUpdatePage, sgDeletePage,
    sgListMembers, sgAddMember, sgUpdateMember, sgRecordPayment, sgRemoveMember,
    adminGetSubgroups, adminCreateSubgroup, adminAssignGroupAdmin, adminDeleteSubgroup, sgUploadImage,
    requestSubgroup, createSubgroupSelf, adminApproveSubgroup, adminRejectSubgroup,
    adminListSubgroupRequests, adminUpdateSubgroupRequest, adminDeleteSubgroupRequest,
    adminClearDismissedRequests, checkSubgroupName,
    getArticleFields, listArticles, getArticle, createArticle, updateArticle, deleteArticle,
    previewArticle, exportArticle, exportArticleUrl, publishArticlePage, sendArticleEmail,
    getPublicArticle, listPublicArticles,
    listSystemTemplates, getSystemTemplate, saveSystemTemplate, resetSystemTemplate, previewSystemTemplate,
    listMeetings, getMeeting, rsvpMeeting, setActionStatus, meetingIcsUrl, meetingsCalendarEvents, meetingsFeedUrl, suggestAgendaItem, decideAgendaSuggestion, meetingCalendarSync,
    adminListFaces, adminNameFace, adminDetectFaces, meetingConsole, addLiveNote, deleteLiveNote, motionHands, uploadRecording, deleteRecording, recordingAudioUrl, adminHistory, adminHistoryExportUrl, structureReportPdfUrl, boardFlyerPdfUrl, flyerConfig, flyerRegenerate, regMotions, regEditMotion, regPayments, regEditPayment, regResendReceipt, regInvoices, regCreateInvoice, regUpdateInvoice, regDeleteInvoice, regSendInvoice, regInvoicePdfUrl, regRemindInvoice, regReminderSettings, regSetReminderSettings, regRunReminders, adminHistoryBackfill, regReminderPreview, birthdaySettings, saveBirthdaySettings, birthdayPreview, birthdayTest, birthdayRun, birthdayUpcoming, getOfficeBearersHistory, adminAlbumSlideshowLink, albumPhotoBook, bookForAlbum, getPhotoBook, photoBookDownloadUrl, adminPhotoBooks, adminPhotoBookForAlbum, adminSavePhotoBook, adminRebuildPhotoBook, adminDeletePhotoBook, adminPhotoBookFileUrl, adminPrintOrders, adminUpdatePrintOrder, adminPrintQuote, adminSendToLulu, adminRefreshLulu, luluConfig, saveLuluConfig, luluTest, memoList, memoMeta, memoCreate, memoUpdate, memoDelete, memoSetPhotos, memoUpload, memoUnlinked, memoAlbumPhotos, memoFromPhoto, memoCopyAi, memoImport, memoImportStatus, memoRetryImages, memoStopImport, memoAlbum, memoPrint, memoAiDraft, memoBundledInfo, memoImportBundled, memoAiBulk, memoAiBulkStatus, memoAiDrafts, memoAiDraftApply, memoAiDraftDismiss, regEmails, regEmailDetail, regDeleteEmail, regResendEmail,
    meetingAgendaPdfUrl, meetingMinutesPdfUrl,
    adminListMeetings, adminCreateMeeting, adminUpdateMeeting, adminDeleteMeeting, adminMarkAttendance,
    emailMeetingMinutes, aiMinutesProse, sendEmailBallots, treasurerReportPdfUrl, saveMeetingMinutes,
    governanceMotions, motionLifecycle,
    govListDocs, govAdminListDocs, govCreateDoc, govExtractDoc, govRematchDoc, govFillContent, govDeleteDoc, govUpdatePolicy, govCarriedMotions,
    adminCreateMotion, adminCloseMotion, adminDeleteMotion, castVote,
    adminMediaList, adminMediaTags, adminMediaUpload, adminMediaUpdate, mediaAbsUrl, adminMediaRegen, adminMediaUsage, adminMediaDelete, adminMediaByImage, adminMediaDownload, adminMediaDownloadZip,
    setContentOverride, clearContentOverride,
    getCatalog, checkout, getPaymentStatus, getMyPayments, getMyInvoices, payInvoice,
    getGoogleConfig, updateGoogleConfig, testGoogle, getGoogleCalendar, getGoogleDocuments,
    createGoogleEvent, previewGoogleSheet, getGoogleSheetTabs, syncMembersFromSheet, previewSheetSync, getSheetSyncLog, resetSheetSyncState,
    getSheetSyncLogDetail, sendTestSyncAlert, testGoogleContacts, previewContactsSync, runContactsSync, getContactsSyncLog,
    getContactsSyncLogDetail, resetContactsSyncState,
    updateUserFields, getDirectory, getMemberProfile, receiptUrl,
    setUserTags, bulkUserTags, getGroupRecipients, sendGroupEmail, getGroupEmailHistory, importMembers, importMembersCsv,
    simulateInbound, getForwardLog, getInboxConfig,
    simulateVoteReply, getVoteLog,
    qaAccountCheck, productionReadiness,
    getReadinessSettings, updateReadinessSettings, sendReadinessReport,
    downloadTestPlan,
    getBrandColors, saveBrandColors, remindNonVoters, getReadinessHistory, getImapSettings, updateImapSettings, testImap, pollImapNow,
    getAbout, saveAbout,
    builtinOverrides, saveBuiltinOverride, resetBuiltinOverride,
    getRenewals, runRenewals, sendRenewal,
    getTemplates, saveTemplate, applyTemplate, deleteTemplate,
    mediaUrl, uploadMyPhoto, addPhotoUrl, addGooglePhoto, setPrimaryPhoto, deletePhoto, clearMyPhotos, updateMyProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
